import React, { useState } from 'react';
import { translations } from '@/context/translations';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableSection } from '../SortableSection';
import { EditableText } from '../EditableText';

export interface TemplateProps {
  cvData: any;
  setCvData: React.Dispatch<React.SetStateAction<any>>;
  fontScale: number;
  avatarUrl: string | null;
  themeColor?: string;
}

export default function Template2({ cvData, setCvData, fontScale, avatarUrl, themeColor = '#333333' }: TemplateProps) {
  const isSq = typeof window !== 'undefined' && window.location.pathname.startsWith('/sq');
  const t = (key: string) => isSq ? ((translations.sq as any)?.[key] || key) : ((translations.en as any)?.[key] || key);
  const leftColRef = React.useRef<HTMLDivElement>(null);
  const rightColRef = React.useRef<HTMLDivElement>(null);
  const [targetHeight, setTargetHeight] = useState<number>(1122);

  const [rightSections, setRightSections] = useState([
    { id: 'aboutMe' },
    { id: 'experience' },
    { id: 'expertise' }
  ]);
  const [editingSkillIndex, setEditingSkillIndex] = useState<number | null>(null);

  const measureContentBottom = (container: HTMLElement | null, rootTop: number) => {
    if (!container) return 0;
    let maxBottom = 0;
    const items = container.querySelectorAll<HTMLElement>(
      'p, h1, h2, h3, h4, span, img, .page-break-item, [class*="Item"], [class*="item"], [class*="Text"], [class*="text"], [class*="dob"], [class*="nameBlock"], [class*="avatar"]'
    );
    items.forEach((el) => {
      if (el.offsetParent === null) return;
      if (
        el.classList.contains('add-btn') ||
        el.closest('.add-btn') ||
        el.classList.contains('drag-handle') ||
        el.closest('.drag-handle') ||
        el.hasAttribute('data-html2canvas-ignore') ||
        el.closest('[data-html2canvas-ignore]')
      ) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const bottom = rect.bottom - rootTop;
      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    });
    return maxBottom;
  };

  React.useLayoutEffect(() => {
    const container = rightColRef.current;
    const canvasRoot = document.getElementById('cv-pdf-content');
    if (!container || !canvasRoot) return;

    // Reset all pushed margins first
    const pushed = container.querySelectorAll<HTMLElement>('[data-pushed]');
    pushed.forEach((el) => {
      el.style.marginTop = '';
      el.removeAttribute('data-pushed');
    });

    const PAGE_HEIGHT = 1122;
    const SAFE_BOTTOM = 85; // Leave 85px bottom margin for logo and spacing
    const TOP_PADDING = 50;  // 50px from page top (30px clear distance below the 40px separator bar)

    const rootRect = canvasRoot.getBoundingClientRect();
    const candidates = container.querySelectorAll<HTMLElement>('.page-break-section, .page-break-item');

    candidates.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const relativeTop = rect.top - rootRect.top;
      const elHeight = rect.height;

      const pageIndex = Math.floor(relativeTop / PAGE_HEIGHT);
      const pageTopZone = pageIndex * PAGE_HEIGHT + TOP_PADDING;
      const pageBottomThreshold = pageIndex * PAGE_HEIGHT + (PAGE_HEIGHT - SAFE_BOTTOM);

      if (pageIndex > 0 && relativeTop < pageTopZone) {
        // Element started too close to top edge of Page 2+
        const push = pageTopZone - relativeTop;
        if (push > 0) {
          el.style.marginTop = `${push}px`;
          el.setAttribute('data-pushed', 'true');
        }
      } else if (relativeTop + Math.min(elHeight, 60) > pageBottomThreshold) {
        // Element crosses into bottom margin of current page -> jump to next page
        const targetTop = (pageIndex + 1) * PAGE_HEIGHT + TOP_PADDING;
        const push = targetTop - relativeTop;
        if (push > 0) {
          el.style.marginTop = `${push}px`;
          el.setAttribute('data-pushed', 'true');
        }
      }
    });

    // Measure actual content bottom
    const leftBottom = measureContentBottom(leftColRef.current, rootRect.top);
    const rightBottom = measureContentBottom(rightColRef.current, rootRect.top);
    const maxContentH = Math.max(leftBottom, rightBottom);

    // Only create page 2 if real content actually extends onto page 2
    let pages = 1;
    if (maxContentH > PAGE_HEIGHT + 35) {
      pages = Math.min(3, Math.ceil(maxContentH / PAGE_HEIGHT));
    }
    setTargetHeight(pages * PAGE_HEIGHT);
  }, [cvData, fontScale, rightSections]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const rightActive = rightSections.find(s => s.id === active.id);
      if (rightActive) {
        setRightSections((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
    }
  };

  const updateArrayField = (field: string, index: number, key: string, val: string) => {
    const newArray = [...(cvData[field] || [])];
    if (newArray[index]) {
      newArray[index] = { ...newArray[index], [key]: val };
      setCvData({ ...cvData, [field]: newArray });
    }
  };

  const addArrayItem = (field: string, defaultItem: any) => {
    const newArray = [...(cvData[field] || []), defaultItem];
    setCvData({ ...cvData, [field]: newArray });
  };

  const removeArrayItem = (field: string, index: number) => {
    const newArray = [...(cvData[field] || [])];
    newArray.splice(index, 1);
    setCvData({ ...cvData, [field]: newArray });
  };

  const toggleVisibility = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const hidden = cvData.hiddenSections || [];
    if (hidden.includes(id)) {
      setCvData({ ...cvData, hiddenSections: hidden.filter((h: string) => h !== id) });
    } else {
      setCvData({ ...cvData, hiddenSections: [...hidden, id] });
    }
  };

  const renderVisibilityToggle = (id: string, isHidden: boolean) => (
    <button
      data-html2canvas-ignore
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => toggleVisibility(id, e)}
      style={{ marginLeft: '8px', background: 'none', border: 'none', color: isHidden ? '#ef4444' : '#a8a29e', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      title={isHidden ? "Show Section" : "Hide Section"}
    >
      {isHidden ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
      )}
    </button>
  );

  const renderRightSection = (id: string, isHidden: boolean) => {
    switch (id) {
      case 'aboutMe':
        if (cvData.aboutMe === undefined) return null;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8em', marginBottom: '1em' }}>
              <div style={{ width: '32px', height: '32px', backgroundColor: themeColor, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg>
              </div>
              <h2 style={{ fontSize: '1.4em', fontWeight: 900, color: themeColor, textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
                <EditableText 
                  value={cvData.sectionTitles?.aboutMe || 'About Me'} 
                  onChange={(val) => setCvData({ ...cvData, sectionTitles: { ...(cvData.sectionTitles || {}), aboutMe: val } })} 
                  style={{ color: themeColor, fontWeight: 900, textTransform: 'uppercase' }}
                />
              </h2>
            </div>
            <div style={{ color: '#444', fontSize: '0.9em', lineHeight: 1.6 }}>
              <EditableText value={cvData.aboutMe || ''} onChange={(val) => setCvData({ ...cvData, aboutMe: val })} multiline maxWords={150} />
            </div>
          </div>
        );
      case 'experience':
        const expList = cvData.workExperience || [];
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8em', marginBottom: '1em' }}>
              <div style={{ width: '32px', height: '32px', backgroundColor: themeColor, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" /></svg>
              </div>
              <h2 style={{ fontSize: '1.4em', fontWeight: 900, color: themeColor, textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
                <EditableText 
                  value={cvData.sectionTitles?.experience || 'Job Experience'} 
                  onChange={(val) => setCvData({ ...cvData, sectionTitles: { ...(cvData.sectionTitles || {}), experience: val } })} 
                  style={{ color: themeColor, fontWeight: 900, textTransform: 'uppercase' }}
                />
              </h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5em' }}>
              {expList.map((exp: any, i: number) => (
                <div key={i} className="page-break-item" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.2em' }}>
                    <EditableText value={exp.title || 'JOB TITLE'} onChange={(val) => updateArrayField('workExperience', i, 'title', val)} style={{ fontWeight: 900, fontSize: '1.1em', color: themeColor, textTransform: 'uppercase' }} />
                    <EditableText value={exp.dates || '2020 - Present'} onChange={(val) => updateArrayField('workExperience', i, 'dates', val)} style={{ fontWeight: 700, fontSize: '0.85em', color: '#444' }} />
                  </div>
                  <div style={{ fontStyle: 'italic', color: '#444', marginBottom: '0.5em', fontSize: '0.85em' }}>
                    <EditableText value={`${exp.company || 'Company'}${exp.location ? ` / ${exp.location}` : ''}`} onChange={(val) => {
                      const parts = val.split('/');
                      updateArrayField('workExperience', i, 'company', parts[0]?.trim() || '');
                      updateArrayField('workExperience', i, 'location', parts[1]?.trim() || '');
                    }} />
                  </div>
                  <div style={{ color: '#444', fontSize: '0.9em', lineHeight: 1.6 }}>
                    <EditableText value={exp.description || 'Description...'} onChange={(val) => updateArrayField('workExperience', i, 'description', val)} multiline />
                  </div>

                  <button
                    data-html2canvas-ignore
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => removeArrayItem('workExperience', i)}
                    style={{ position: 'absolute', top: 0, right: '-25px', background: '#fee2e2', border: '1px solid #f87171', color: '#dc2626', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                    title="Remove Experience"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: expList.length > 0 ? '0.2rem' : '0' }}>
              <button
                className="add-btn"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => addArrayItem('workExperience', { title: 'New Job Title', dates: 'Year - Year', location: 'Location', company: 'Company Name', description: 'Job description...' })}
                style={{ background: 'none', border: '1px dashed #d6d3d1', color: '#a8a29e', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}
              >
                {t('+ Add Experience')}
              </button>
            </div>
          </div>
        );
      case 'expertise':
        const skillsList = cvData.skills || [];
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8em', marginBottom: '1em' }}>
              <div style={{ width: '32px', height: '32px', backgroundColor: themeColor, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" /></svg>
              </div>
              <h2 style={{ fontSize: '1.4em', fontWeight: 900, color: themeColor, textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
                <EditableText 
                  value={cvData.sectionTitles?.skills || 'Skills'} 
                  onChange={(val) => setCvData({ ...cvData, sectionTitles: { ...(cvData.sectionTitles || {}), skills: val } })} 
                  style={{ color: themeColor, fontWeight: 900, textTransform: 'uppercase' }}
                />
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '2em', rowGap: '1.5em' }}>
              <style dangerouslySetInnerHTML={{ __html: `
                .skill-row-container-2 .skill-actions-2 { opacity: 0; transition: opacity 0.2s; pointer-events: none; }
                .skill-row-container-2:hover .skill-actions-2 { opacity: 1; pointer-events: auto; }
              ` }} />
              {skillsList.map((s: any, i: number) => (
                <div key={i} className="skill-row-container-2" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 45px 12px 0', margin: '-12px -45px -12px 0' }}>
                  <div style={{ width: '50%' }}>
                    <EditableText
                      value={typeof s === 'string' ? s : (s.name || 'New Skill')}
                      onChange={(val) => {
                        const newSkills = [...cvData.skills];
                        const currentLevel = typeof s === 'string' ? 80 : (s.level || 80);
                        newSkills[i] = { name: val, level: currentLevel };
                        setCvData({ ...cvData, skills: newSkills });
                      }}
                      style={{ fontSize: '0.95em', fontWeight: 800, color: themeColor }}
                    />
                  </div>
                  <div style={{ width: '45%', position: 'relative' }}>
                    <div style={{ width: '100%', height: '5px', backgroundColor: '#d1d5db', borderRadius: '3px' }}>
                      <div style={{ width: `${typeof s === 'string' ? 80 : (s.level || 80)}%`, height: '100%', backgroundColor: themeColor, borderRadius: '3px', transition: 'width 0.2s ease' }}></div>
                    </div>
                    {editingSkillIndex === i && (
                      <div
                        data-html2canvas-ignore
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: '-32px',
                          right: '0px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          zIndex: 20,
                          cursor: 'default'
                        }}
                      >
                        <input
                          type="number"
                          min="0" max="100"
                          value={typeof s === 'string' ? 80 : (s.level || 80)}
                          onChange={(e) => {
                            const newSkills = [...cvData.skills];
                            const currentName = typeof s === 'string' ? s : (s.name || 'New Skill');
                            newSkills[i] = { name: currentName, level: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) };
                            setCvData({ ...cvData, skills: newSkills });
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          style={{
                            width: '60px',
                            padding: '2px 4px',
                            fontSize: '0.9em',
                            border: '1px solid #9ca3af',
                            borderRadius: '4px',
                            boxSizing: 'border-box',
                            textAlign: 'center',
                            backgroundColor: 'white'
                          }}
                          placeholder="%"
                          title="Skill Level (0-100)"
                        />
                        <button
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => setEditingSkillIndex(null)}
                          style={{
                            fontSize: '0.85em',
                            padding: '4px 10px',
                            backgroundColor: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="skill-actions-2" style={{ position: 'absolute', top: '-10px', right: '10px', display: 'flex', gap: '4px', zIndex: 10 }}>
                    <button
                      data-html2canvas-ignore
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => setEditingSkillIndex(editingSkillIndex === i ? null : i)}
                      style={{ background: editingSkillIndex === i ? '#dbeafe' : '#f3f4f6', border: editingSkillIndex === i ? '1px solid #93c5fd' : '1px solid #d1d5db', color: editingSkillIndex === i ? '#2563eb' : '#4b5563', cursor: 'pointer', padding: '2px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title={editingSkillIndex === i ? "Done editing" : "Edit skill level"}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    </button>
                    <button
                      data-html2canvas-ignore
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => removeArrayItem('skills', i)}
                      style={{ background: '#fee2e2', border: '1px solid #f87171', color: '#dc2626', cursor: 'pointer', padding: '2px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title="Remove Skill"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: skillsList.length > 0 ? '0.8rem' : '0' }}>
              <button
                className="add-btn"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => addArrayItem('skills', { name: 'New Skill', level: 80 })}
                style={{ background: 'none', border: '1px dashed #d6d3d1', color: '#a8a29e', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}
              >
                {t('+ Add Skill')}
              </button>
            </div>

          </div>
        );
      default:
        return null;
    }
  };

  const langs = cvData.languages || [];
  const eduList = cvData.education || [];

  return (
    <div id="cv-pdf-content" style={{ display: 'grid', gridTemplateColumns: '260px 1fr', width: '794px', minHeight: `${targetHeight}px`, height: `${targetHeight}px`, background: 'white', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden', position: 'relative', fontSize: `${Math.round(13 * fontScale)}px`, fontFamily: 'Helvetica, Arial, sans-serif' }}>

      {/* STATIC LEFT COLUMN */}
      <div ref={leftColRef} style={{ backgroundColor: '#eef0f3', display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Top Block: Name + Avatar */}
        <div style={{ position: 'relative', width: '100%', backgroundColor: themeColor, borderBottomLeftRadius: '130px', borderBottomRightRadius: '130px', paddingBottom: '130px', zIndex: 1 }}>
          <div style={{ padding: '3.5em 1.5em 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <EditableText
              value={cvData.name || 'NAME'}
              onChange={(val) => setCvData({ ...cvData, name: val })}
              style={{ color: 'white', fontWeight: 900, fontSize: (cvData.name || 'NAME').length > 10 ? `${2.2 * (10 / (cvData.name || 'NAME').length)}em` : '2.2em', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.1, width: '100%', wordBreak: 'break-word' }}
            />
            <EditableText
              value={cvData.surname || 'SURNAME'}
              onChange={(val) => setCvData({ ...cvData, surname: val })}
              style={{ color: 'white', fontWeight: 900, fontSize: (cvData.surname || 'SURNAME').length > 10 ? `${2.2 * (10 / (cvData.surname || 'SURNAME').length)}em` : '2.2em', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.1, width: '100%', wordBreak: 'break-word', marginTop: '0.2em' }}
            />
            <EditableText
              value={cvData.profession || 'PROFESSION'}
              onChange={(val) => setCvData({ ...cvData, profession: val })}
              style={{ color: '#ccc', fontWeight: 300, fontSize: '0.9em', textAlign: 'center', letterSpacing: '2px', textTransform: 'uppercase', marginTop: '0.5em', width: '100%', wordBreak: 'break-word' }}
            />

            {/* Spacer to prevent avatar from overlapping text */}
            <div style={{ height: '110px', width: '100%' }}></div>
          </div>

          {/* Avatar perfectly centered in the 130px semi-circle */}
          {/* Avatar is 200px, radius 100px. Semi-circle is 130px radius. */}
          {/* To make them concentric, avatar center must be 130px from bottom. So bottom = 130 - 100 = 30px. */}
          <label className="avatar-upload-container-2" style={{
            width: '200px', height: '200px', borderRadius: '50%', border: '8px solid white',
            position: 'absolute', bottom: '30px', left: 0, right: 0, margin: '0 auto',
            backgroundColor: '#44403c', zIndex: 2, overflow: 'hidden', cursor: 'pointer', display: 'block'
          }}>
            <style dangerouslySetInnerHTML={{ __html: `
              .avatar-upload-overlay-2 { opacity: 0; transition: opacity 0.2s; position: absolute; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; color: white; pointer-events: none; }
              .avatar-upload-container-2:hover .avatar-upload-overlay-2 { opacity: 1; }
            ` }} />
            <input
              type="file"
              accept=".jpg, .jpeg, .png, .webp, .heic, .heif, image/jpeg, image/png, image/webp, image/heic, image/heif"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setCvData({ ...cvData, customAvatar: reader.result as string });
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
            {cvData.customAvatar || avatarUrl ? (
              <img src={cvData.customAvatar || avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : null}
            <div className="avatar-upload-overlay-2" data-html2canvas-ignore>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
            </div>
          </label>
        </div>

        {/* Middle Block: Contact */}
        <div style={{ padding: '2.5em 1.5em', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6em', marginBottom: '1.2em', paddingLeft: '1.5em' }}>
            <div style={{ width: '28px', height: '28px', backgroundColor: themeColor, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg>
            </div>
            <h2 style={{ fontSize: '1.2em', fontWeight: 900, color: themeColor, margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>Contact Me</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9em', width: '100%', fontSize: '0.95em', color: '#444', paddingLeft: '2em' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8em' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
              <EditableText value={cvData.phone || ''} onChange={(val) => setCvData({ ...cvData, phone: val })} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8em' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><path d="M22 6L12 13L2 6"></path></svg>
              <EditableText value={cvData.email || ''} onChange={(val) => setCvData({ ...cvData, email: val })} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8em' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
              <EditableText
                value={[cvData.address, cvData.city, cvData.country].filter(Boolean).join(', ') || ''}
                onChange={(val) => {
                  const parts = val.split(',').map(s => s.trim());
                  setCvData({ ...cvData, address: parts[0] || '', city: parts[1] || '', country: parts[2] || '' });
                }}
              />
            </div>
          </div>
        </div>
        {/* Bottom Block: Education & Languages */}
        <div style={{ backgroundColor: themeColor, borderTopLeftRadius: '130px', borderTopRightRadius: '130px', padding: '8em 3em 2em', marginTop: 'auto', color: 'white', flex: 1 }}>

          {/* Education */}
          <div style={{ marginBottom: '2.5em' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6em', marginBottom: '1.2em' }}>
              <div style={{ width: '28px', height: '28px', backgroundColor: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" /></svg>
              </div>
              <h2 style={{ fontSize: '1.2em', fontWeight: 900, color: 'white', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                <EditableText 
                  value={cvData.sectionTitles?.education || 'Education'} 
                  onChange={(val) => setCvData({ ...cvData, sectionTitles: { ...(cvData.sectionTitles || {}), education: val } })} 
                  style={{ color: 'white', fontWeight: 900, textTransform: 'uppercase' }}
                />
              </h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2em' }}>
              {eduList.map((e: any, i: number) => (
                <div key={i} className="page-break-item" style={{ position: 'relative' }}>
                  <EditableText value={e.degree || e.school || 'Degree / School'} onChange={(val) => updateArrayField('education', i, 'degree', val)} style={{ fontWeight: 800, fontSize: '1em', color: 'white', textTransform: 'uppercase', marginBottom: '0.2em', display: 'block' }} />
                  <EditableText value={e.school && e.degree ? e.school : ''} onChange={(val) => updateArrayField('education', i, 'school', val)} style={{ fontSize: '0.85em', color: '#ddd', display: e.school && e.degree ? 'block' : 'none', marginBottom: '0.2em' }} />
                  <EditableText value={e.dates || 'Year - Year'} onChange={(val) => updateArrayField('education', i, 'dates', val)} style={{ fontSize: '0.85em', color: '#ddd', display: 'block' }} />

                  <button
                    data-html2canvas-ignore
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => removeArrayItem('education', i)}
                    style={{ position: 'absolute', top: 0, right: '-15px', background: '#fee2e2', border: '1px solid #f87171', color: '#dc2626', cursor: 'pointer', padding: '2px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                    title="Remove Education"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: eduList.length > 0 ? '0.8rem' : '0' }}>
              <button
                className="add-btn"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => addArrayItem('education', { school: 'University/School', dates: 'Year - Year', degree: 'Degree Name', description: 'Description...' })}
                style={{ background: 'none', border: '1px dashed #aaa', color: '#ccc', padding: '2px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75em' }}
              >
                {t('+ Add Education')}
              </button>
            </div>
          </div>

          {/* Languages */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6em', marginBottom: '1.2em' }}>
              <div style={{ width: '28px', height: '28px', backgroundColor: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" /></svg>
              </div>
              <h2 style={{ fontSize: '1.2em', fontWeight: 900, color: 'white', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                <EditableText 
                  value={cvData.sectionTitles?.languages || 'Language'} 
                  onChange={(val) => setCvData({ ...cvData, sectionTitles: { ...(cvData.sectionTitles || {}), languages: val } })} 
                  style={{ color: 'white', fontWeight: 900, textTransform: 'uppercase' }}
                />
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
              {langs.map((l: any, i: number) => (
                <div key={i} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                  <div style={{ width: '5px', height: '5px', backgroundColor: 'white', borderRadius: '50%' }}></div>
                  <EditableText
                    value={l.language || 'Language'}
                    onChange={(val) => updateArrayField('languages', i, 'language', val)}
                    style={{ fontSize: '0.85em', fontWeight: 800, color: 'white', textTransform: 'uppercase' }}
                  />
                  <button
                    data-html2canvas-ignore
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => removeArrayItem('languages', i)}
                    style={{ position: 'absolute', top: '-4px', right: '-10px', background: '#fee2e2', border: '1px solid #f87171', color: '#dc2626', cursor: 'pointer', padding: '2px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                    title="Remove Language"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: langs.length > 0 ? '0.8rem' : '0' }}>
              <button
                className="add-btn"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => addArrayItem('languages', { language: 'New Language' })}
                style={{ background: 'none', border: '1px dashed #aaa', color: '#ccc', padding: '2px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75em' }}
              >
                {t('+ Add Language')}
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* DRAGGABLE RIGHT COLUMN */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div ref={rightColRef} style={{ padding: '3.5em 2.5em', display: 'flex', flexDirection: 'column', gap: '1.2em' }}>
          <SortableContext items={rightSections} strategy={verticalListSortingStrategy}>
            {rightSections.map((section) => {
              const isHidden = (cvData.hiddenSections || []).includes(section.id);
              const content = renderRightSection(section.id, isHidden);
              if (!content) return null;
              return (
                <SortableSection key={section.id} id={section.id} disabled={editingSkillIndex !== null}>
                  <div className="page-break-section" style={{ position: 'relative', border: '2px dashed transparent', borderRadius: '8px', padding: '1em', margin: '-1em', opacity: isHidden ? 0.4 : 1 }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                    data-html2canvas-ignore={isHidden ? "true" : undefined}
                  >
                    <div data-html2canvas-ignore style={{ position: 'absolute', right: '12px', top: '12px', display: 'flex', alignItems: 'center', gap: '4px', zIndex: 10 }}>
                      {renderVisibilityToggle(section.id, isHidden)}
                      <div style={{ opacity: 0.3, cursor: 'grab', display: 'flex' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></svg>
                      </div>
                    </div>
                    {content}
                  </div>
                </SortableSection>
              );
            })}
          </SortableContext>
        </div>
      </DndContext>
      {/* Page Break Separators - Rendered ONLY if more than 1 page exists */}
      {targetHeight > 1122 && (
        <div data-html2canvas-ignore style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 100, overflow: 'hidden' }}>
          {[...Array(Math.floor(targetHeight / 1122) - 1)].map((_, i) => {
            const pageNum = i + 1;
            return (
              <div
                key={pageNum}
                style={{
                  position: 'absolute',
                  top: `${pageNum * 1122 - 20}px`,
                  left: '-32px',
                  right: '-32px',
                  height: '40px',
                  backgroundColor: '#f3f4f6',
                  boxShadow: '0 6px 10px -3px rgba(0, 0, 0, 0.1), 0 -6px 10px -3px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderTop: '2px solid #e5e7eb',
                  borderBottom: '2px solid #e5e7eb'
                }}
              >
                <span
                  style={{
                    background: '#ffffff',
                    color: '#4b5563',
                    padding: '4px 18px',
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.6px',
                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #d1d5db',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#2563eb' }}></span>
                  PAGE {pageNum + 1}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Repeating Bottom Logo for each A4 Page */}
      <div style={{ position: 'absolute', top: 0, left: '260px', right: 0, bottom: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {[...Array(Math.max(1, Math.floor(targetHeight / 1122)))].map((_, i) => (
          <div key={i} style={{ position: 'absolute', top: `${(i + 1) * 1122 - 35}px`, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
            <img src="/logo.png" alt="Logo" style={{ height: '20px', opacity: 0.15, filter: 'grayscale(100%)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}
